import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FaSearch, FaFilter, FaServer, FaNetworkWired, FaCalendarAlt, FaSync, FaChevronDown, FaEye } from 'react-icons/fa';
import LogPathRow from './LogPathRow';
import { useLogContext } from '../../context/LogContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { useTheme } from '../../context/ThemeContext';

/**
 * LogMonitor Component
 * 
 * Main component for displaying log data in a hierarchical structure.
 * Displays log paths, files, and events with filtering capabilities.
 * Features real-time updates, optimized rendering, and persistent state.
 * 
 * @component
 * @param {Object} props
 * @param {string} [props.serverId] - Optional server ID to filter logs for a specific server
 */
const LogMonitor = ({ serverId }) => {
  const { logData, forceUpdate, forceUpdateCounter, lastUpdate } = useLogContext();
  const { connectionStatus } = useWebSocket();
  const { theme, isDarkTheme } = useTheme();
  
  // State for filter dropdown
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  
  // Refs for performance optimization
  const updateTimerRef = useRef(null);
  const lastRenderTimeRef = useRef(Date.now());
  const pendingUpdatesRef = useRef(false);
  
  // State for expanded paths
  const [expandedPaths, setExpandedPaths] = useState(() => {
    try {
      const stored = localStorage.getItem('logMonitor_expandedPaths');
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      console.error('Error loading expanded paths from localStorage:', e);
      return {};
    }
  });
  
  // Filters state with localStorage persistence
  const [filters, setFilters] = useState(() => {
    try {
      const stored = localStorage.getItem('logMonitor_filters');
      return stored ? JSON.parse(stored) : {
        level: [], // Array of level values to filter by, empty means all
        search: '', // Search text
        showArchived: false // Whether to show archived logs
      };
    } catch (e) {
      console.error('Error loading filters from localStorage:', e);
      return {
        level: [],
        search: '',
        showArchived: false
      };
    }
  });
  
  // State for UI feedback
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  
  // Save expanded paths to localStorage when changed
  useEffect(() => {
    try {
      localStorage.setItem('logMonitor_expandedPaths', JSON.stringify(expandedPaths));
    } catch (e) {
      console.error('Error saving expanded paths to localStorage:', e);
    }
  }, [expandedPaths]);
  
  // Save filters to localStorage when changed
  useEffect(() => {
    try {
      localStorage.setItem('logMonitor_filters', JSON.stringify(filters));
    } catch (e) {
      console.error('Error saving filters to localStorage:', e);
    }
  }, [filters]);
  
  // Listen for log-data-updated events for real-time updates
  useEffect(() => {
    const handleLogDataUpdated = (event) => {
      // Throttle updates to prevent excessive re-renders
      if (!pendingUpdatesRef.current) {
        pendingUpdatesRef.current = true;
        
        // Show updating indicator
        setIsUpdating(true);
        
        // Clear any existing timer
        if (updateTimerRef.current) {
          clearTimeout(updateTimerRef.current);
        }
        
        // Schedule update after a short delay to batch multiple rapid updates
        updateTimerRef.current = setTimeout(() => {
          setLastUpdateTime(Date.now());
          setIsUpdating(false);
          pendingUpdatesRef.current = false;
        }, 300); // 300ms debounce
      }
    };
    
    window.addEventListener('log-data-updated', handleLogDataUpdated);
    return () => {
      window.removeEventListener('log-data-updated', handleLogDataUpdated);
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
    };
  }, []);
  
  // Update when forceUpdateCounter changes (from LogContext)
  useEffect(() => {
    if (forceUpdateCounter > 0) {
      // Only update if it's been more than 500ms since the last render
      const now = Date.now();
      if (now - lastRenderTimeRef.current > 500) {
        setLastUpdateTime(now);
        lastRenderTimeRef.current = now;
      }
    }
  }, [forceUpdateCounter]);
  
  /**
   * Toggle expanded state for a log path
   * 
   * @param {string} path - The log path to toggle
   */
  const handleTogglePath = useCallback((path) => {
    setExpandedPaths(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  }, []);
  
  /**
   * Force a manual refresh of log data
   */
  const handleManualRefresh = useCallback(() => {
    setIsUpdating(true);
    forceUpdate();
    
    // Reset updating state after a short delay
    setTimeout(() => {
      setIsUpdating(false);
      setLastUpdateTime(Date.now());
    }, 300);
  }, [forceUpdate]);
  
  /**
   * Update filter value
   * 
   * @param {string} key - Filter key to update
   * @param {*} value - New value for the filter
   */
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    
  }, [forceUpdate]);
  
  /**
   * Toggle a log level filter
   * 
   * @param {string} level - Log level to toggle
   */
  const toggleLevelFilter = useCallback((level) => {
    // Set updating indicator before filter change
    setIsUpdating(true);
    
    setFilters(prev => {
      const newLevels = prev.level.includes(level)
        ? prev.level.filter(l => l !== level)
        : [...prev.level, level];
      return {
        ...prev,
        level: newLevels
      };
    });
    
    // Reset updating state after a short delay for visual feedback
    setTimeout(() => {
      setIsUpdating(false);
      setLastUpdateTime(Date.now());
    }, 150);
  }, [forceUpdate]);
  
  /**
   * Filter log data based on current filters
   * If serverId is provided, only show logs for that server
   */
  const filteredLogData = useMemo(() => {
    console.log("Recomputing filteredLogData with filters:", filters);
    // If no log data, return empty object
    if (!logData || Object.keys(logData).length === 0) {
      return {};
    }
    
    // Start with all log data or filtered by serverId if provided
    let filtered = {};
    
    if (serverId) {
      // Only include the specified server
      if (logData[serverId]) {
        filtered[serverId] = { ...logData[serverId] };
      }
    } else {
      // Include all servers
      filtered = { ...logData };
    }
    
    // Apply search filter if specified
    if (filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      
      Object.keys(filtered).forEach(sid => {
        const serverLogs = { ...filtered[sid] };
        
        // Check each log path
        Object.keys(serverLogs).forEach(path => {
          // Check if path matches search
          const pathMatches = path.toLowerCase().includes(searchTerm);
          
          // For each file, filter events but keep the file structure
          const filteredFiles = {};
          
          Object.entries(serverLogs[path].files).forEach(([fileName, fileData]) => {
            // Check if file name matches search
            const fileNameMatches = fileName.toLowerCase().includes(searchTerm);
            
            // Check if any event in the file matches search
            const matchingEvents = fileData.events.filter(event => 
              event.message.toLowerCase().includes(searchTerm) ||
              (event.host && event.host.toLowerCase().includes(searchTerm))
            );
            
            // Always keep the file, but with filtered events
            filteredFiles[fileName] = {
              ...fileData,
              events: matchingEvents,
              // Add a flag to indicate if this file has any matches
              hasMatches: fileNameMatches || matchingEvents.length > 0
            };
          });
          
          // Always keep the path, but update with filtered files
          serverLogs[path] = {
            ...serverLogs[path],
            files: filteredFiles,
            // Add a flag to indicate if this path or any of its files have matches
            hasMatches: pathMatches || Object.values(filteredFiles).some(file => file.hasMatches)
          };
        });
        
        filtered[sid] = serverLogs;
      });
    }
    
    // Apply level filter if specified
    if (filters.level.length > 0) {
      Object.keys(filtered).forEach(sid => {
        const serverLogs = { ...filtered[sid] };
        // For each log path, always keep the path and files, but filter events in each file
        Object.keys(serverLogs).forEach(path => {
          const filteredFiles = {};
          Object.entries(serverLogs[path].files).forEach(([fileName, fileData]) => {
            // Filter events by level
            const matchingEvents = fileData.events.filter(event => 
              filters.level.includes(event.level)
            );
            // Always include the file, even if there are no matching events
            filteredFiles[fileName] = {
              ...fileData,
              events: matchingEvents,
              // Add a flag to indicate if this file has any matches
              hasMatches: matchingEvents.length > 0
            };
          });
          
          // Always keep the path with filtered files
          serverLogs[path] = {
            ...serverLogs[path],
            files: filteredFiles,
            // Add a flag to indicate if any files have matches
            hasMatches: Object.values(filteredFiles).some(file => file.hasMatches)
          };
        });
        filtered[sid] = serverLogs;
      });
    }
    // Note: This ensures file rows and path headers are always rendered, even if no events match the level filter. The table will show an empty state row if no events match.
    
    // Apply archived filter if needed
    if (!filters.showArchived) {
      Object.keys(filtered).forEach(sid => {
        const serverLogs = { ...filtered[sid] };
        
        // Check each log path
        Object.keys(serverLogs).forEach(path => {
          // If path is archived, remove it
          if (serverLogs[path].archived) {
            delete serverLogs[path];
            return;
          }
          
          // Check each file
          const filteredFiles = {};
          let hasNonArchivedFiles = false;
          
          Object.entries(serverLogs[path].files).forEach(([fileName, fileData]) => {
            // If file is not archived, keep it
            if (!fileData.archived) {
              hasNonArchivedFiles = true;
              filteredFiles[fileName] = fileData;
            }
          });
          
          // If no non-archived files, remove the path
          if (!hasNonArchivedFiles) {
            delete serverLogs[path];
          } else {
            // Update files with filtered version
            serverLogs[path] = {
              ...serverLogs[path],
              files: filteredFiles
            };
          }
        });
        
        filtered[sid] = serverLogs;
      });
    }
    
    return filtered;
  }, [logData, serverId, filters]);
  
  // Flatten the filtered log data for easier rendering
  const flattenedLogData = useMemo(() => {
    const flattened = [];
    
    Object.entries(filteredLogData).forEach(([sid, serverLogs]) => {
      Object.entries(serverLogs).forEach(([path, logPath]) => {
        flattened.push({
          serverId: sid,
          path,
          ...logPath
        });
      });
    });
    
    return flattened;
  }, [filteredLogData]);
  
  // Check if there are any matching events across all paths and files
  const hasAnyMatches = useMemo(() => {
    // If no search or level filters are applied, all data matches
    if (!filters.search.trim() && filters.level.length === 0) {
      return true;
    }
    
    // Check if any path has matches
    return flattenedLogData.some(logPath => logPath.hasMatches);
  }, [flattenedLogData, filters]);
  
  // Check if there's any log data to display and distinguish between no data and filtered data
  const hasAnyData = useMemo(() => {
    return Object.keys(logData || {}).length > 0;
  }, [logData]);
  
  // Check if there's any filtered log data to display
  const hasFilteredData = useMemo(() => {
    return flattenedLogData.length > 0;
  }, [flattenedLogData]);
  
  // Toggle filter dropdown
  const toggleFilterDropdown = useCallback(() => {
    setShowFilterDropdown(prev => !prev);
  }, []);
  
  // Close filter dropdown when clicking outside
  const filterDropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };
  
  return (
    <div className="log-monitor">
      <div className="log-monitor-container">
        {/* Floating controls */}
        <div className="log-monitor-floating-controls">
          {/* Search area */}
          <div className="log-monitor-search">
            <FaSearch />
            <input
              type="text"
              placeholder="Search logs..."
              value={filters.search}
              onChange={(e) => {
                setIsUpdating(true);
                updateFilter('search', e.target.value);
                // Debounce the search input to avoid too many re-renders
                if (updateTimerRef.current) {
                  clearTimeout(updateTimerRef.current);
                }
                updateTimerRef.current = setTimeout(() => {
                  setIsUpdating(false);
                  setLastUpdateTime(Date.now());
                }, 300);
              }}
            />
            {filters.search && (
              <button className="clear-search" onClick={() => {
                setIsUpdating(true);
                updateFilter('search', '');
                setTimeout(() => {
                  setIsUpdating(false);
                  setLastUpdateTime(Date.now());
                }, 150);
              }}>
                <span>×</span>
              </button>
            )}
          </div>
          {/* Filter dropdown */}
          <div className="log-monitor-filter-dropdown" ref={filterDropdownRef}>
            <button 
              className={`filter-button ${filters.level.length > 0 || !filters.showArchived ? 'active' : ''}`}
              onClick={toggleFilterDropdown}
              title="Filter logs by level and more"
            >
              <FaFilter />
              <span className="filter-text">Filters {filters.level.length > 0 || !filters.showArchived ? `(${filters.level.length + (!filters.showArchived ? 1 : 0)})` : ''}</span>
              <FaChevronDown className={`dropdown-chevron ${showFilterDropdown ? 'open' : ''}`} />
            </button>
            {showFilterDropdown && (
              <div className="filter-dropdown-content">
                {/* Log Level filter */}
                <div className="filter-section">
                  <div className="filter-section-header">
                    <h4>Log Level</h4>
                    <button 
                      className="clear-section-filters"
                      onClick={() => {
                        setIsUpdating(true);
                        setFilters({ ...filters, level: [] });
                        setTimeout(() => {
                          setIsUpdating(false);
                          setLastUpdateTime(Date.now());
                        }, 150);
                      }}
                      disabled={filters.level.length === 0}
                    >
                      Clear
                    </button>
                  </div>
                  <div className="filter-options">
                    {['Debug', 'Info', 'Warning', 'Error', 'Critical', 'SegmentFault'].map(level => (
                      <label key={level} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={filters.level.includes(level)}
                          onChange={() => toggleLevelFilter(level)}
                        />
                        <span className={`level-indicator level-${level.toLowerCase()}`}></span>
                        {level}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Show archived filter */}
                <div className="filter-section">
                  <div className="filter-options">
                    <label className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={filters.showArchived}
                        onChange={() => {
                          setIsUpdating(true);
                          setFilters({ ...filters, showArchived: !filters.showArchived });
                          setTimeout(() => {
                            setIsUpdating(false);
                            setLastUpdateTime(Date.now());
                          }, 150);
                        }}
                      />
                      Show archived logs
                    </label>
                  </div>
                </div>
                {/* Clear all filters button */}
                <div className="filter-actions">
                  <button 
                    className="clear-all-filters"
                    onClick={() => {
                      setIsUpdating(true);
                      setFilters({ level: [], search: '', showArchived: false });
                      // Force a recomputation of the filtered data
                      forceUpdate();
                      setTimeout(() => {
                        setIsUpdating(false);
                        setLastUpdateTime(Date.now());
                      }, 150);
                    }}
                    disabled={filters.level.length === 0 && filters.search === '' && !filters.showArchived}
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Status indicators */}
          <div className="log-monitor-status-indicators">
            <div className={`connection-status ${connectionStatus.toLowerCase()}`}>
              {connectionStatus}
            </div>
            <div className="last-update-time">
              Last update: {formatTimestamp(lastUpdateTime)}
            </div>
          </div>

          {/* Refresh button */}
          <button 
            className={`refresh-button ${isUpdating ? 'updating' : ''}`}
            onClick={handleManualRefresh}
            disabled={isUpdating}
            title="Refresh log data"
          >
            <FaSync className={isUpdating ? 'spin' : ''} />
            <span className="filter-text">Refresh</span>
          </button>
        </div>
        {/* Main log monitor content */}
        <div className="log-monitor-content">
          {flattenedLogData.length > 0 ? (
            flattenedLogData.map((logPath) => (
              <LogPathRow
                key={logPath.serverId + ':' + logPath.path}
                logPath={logPath}
                expanded={!!expandedPaths[logPath.path]}
                onToggle={() => handleTogglePath(logPath.path)}
                showArchived={filters.showArchived}
                lastUpdate={lastUpdate}
                filters={filters}
              />
            ))
          ) : (
            <div className="log-monitor-empty">
              <div className="log-monitor-empty-icon">
                <FaEye />
              </div>
              <div>
                {hasAnyData ? 
                  (filters.search || filters.level.length > 0) ? 
                    "No logs match your current filters. Try adjusting your search criteria." : 
                    "No logs to display. Check your data source."
                  : 
                  "No log data available. Check your data source connection."
                }
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogMonitor;
